'use client';
import Heading from '@/components/Heading';
import { Col, Flex, Image, Row } from 'antd';
import moment from 'moment';
import Text from '@componentsText';
import { mobileBreakPoint, withFullPath } from '@/utils/utils';
import PageHeader from '@/components/PageHeader';
import PartyCardList from '@/components/PartyCardList';
import useWindowSize from '@utils/useScreenSize';
import CustButton, { GeneralButtonType } from '../Button';
import { useGlobalStore } from '@/hooks/useGlobalStore';
import { useEffect } from 'react';
import { CrossSellBannerFragment, PartyInformationFragment } from '@/@types/generated/sitecore';
import CrossSellBanner from '../CrossSellBanner';
import { WATrackerClickEvn, WATrackerTrackPageView, getButtonLinkWithCID } from '@/utils/wa';
import WaveRightIcon from '../SVG/WaveRightIcon';
import WaveLeftIcon from '../SVG/WaveLeftIcon';
import RichText from '../RichText';
import { useTranslation } from '@i18n/use-transition.client';
import AddToCalendar from '../AddToCalendar';
import ImagePreview from '../ImagePreview';

interface Props {
    contentData: any;
    currentParties: PartyInformationFragment[];
    upcoming: PartyInformationFragment[];
    previous: PartyInformationFragment[];
    crossSellBannerData: CrossSellBannerFragment;
}

/** 适用于 party page  */
const PartiesPage = (props: Props) => {
    const windowSize = useWindowSize();
    const globalStore = useGlobalStore();
    const { t } = useTranslation();
    const { contentData, currentParties, upcoming, previous, crossSellBannerData } = props;

    const themeColor = globalStore.currentTheme?.themeColor;

    const fontColor = globalStore.currentTheme?.fontColor;

    const highlightColor = globalStore.currentTheme?.highlightColor;

    const musicWaveColor = globalStore.currentTheme?.musicWaveColor;

    const TITLE_ICON = {
        titleIconRight: () => <WaveRightIcon iconColor={musicWaveColor}></WaveRightIcon>,
        titleIconLeft: () => <WaveLeftIcon iconColor={musicWaveColor}></WaveLeftIcon>,
    };

    const TitleIcon = contentData?.titleIconOptions?.value
        ? TITLE_ICON[contentData?.titleIconOptions?.value as keyof typeof TITLE_ICON]
        : () => null;

    useEffect(() => {
        WATrackerTrackPageView(`RnE/HW/${contentData?.name}/Home`);
        globalStore.setGlobalStore({ pageID: `${contentData?.name}` });
    }, []);

    const dateList =
        currentParties[0] &&
        currentParties[0]?.partyDates[0] &&
        currentParties[0]?.partyDates[0].dateList;

    const calendarData =
        dateList?.length > 0
            ? dateList.map((i) => {
                  const startTime =
                      (i.partyDateTime?.dateValue && i.partyDateTime?.dateValue < 0) ||
                      !i.partyDateTime?.dateValue
                          ? undefined
                          : i.partyDateTime?.dateValue;
                  const endTime =
                      (i.partyEndDateTime?.dateValue && i.partyEndDateTime?.dateValue < 0) ||
                      !i.partyEndDateTime?.dateValue
                          ? undefined
                          : i.partyEndDateTime?.dateValue;
                  return {
                      name: i.partyCardTitle?.value,
                      description: '',
                      startDate: moment(startTime).format('yyyy-MM-DD'),
                      startTime: moment(startTime).format('HH:mm:ss'),
                      endDate: moment(endTime).format('yyyy-MM-DD'),
                      endTime: moment(endTime).format('HH:mm:ss'),
                  };
              })
            : [];

    return (
        <>
            <PageHeader
                pageTitle={contentData?.pageTitle?.value || ''}
                pageDescription={contentData?.pageDescription?.rendered || ''}
                pageBgimg={contentData?.headerBgImage?.src || ''}
                // pageName={`contentData?.name`}
            />
            <Flex vertical justify='center' align='center' className=' bg-primary-bg-color'>
                <Flex vertical className='max-w-[1920px] xmd:py-[94px] xmd:px-[80px] px-5 py-10'>
                    {/* current */}
                    {currentParties.length > 0 && (
                        <Row gutter={[24, 40]}>
                            <Col xs={24} md={12}>
                                <ImagePreview
                                    setting={{
                                        src: withFullPath(currentParties[0].partyImage?.src),
                                        rootClassName: 'rounded-[5px] w-full',
                                    }}
                                    canPreview
                                />
                            </Col>
                            <Col xs={24} md={12}>
                                <Flex vertical className={'xmd:pl-8'}>
                                    <Flex vertical gap={16}>
                                        <Heading
                                            className={
                                                '!text-base-font-color mb-[8px] !text-[26px]'
                                            }
                                            level={5}
                                            text={t('partiesCurrent')}
                                        />
                                        <TitleIcon />
                                        <Heading
                                            className={'!text-white !text-[40px]'}
                                            style={{ lineHeight: '50px' }}
                                            level={4}
                                            text={currentParties[0].partyName?.value}
                                        />
                                        <div className='flex'>
                                            <Heading
                                                level={5}
                                                text={currentParties[0].partyDateText?.value}
                                                className={'text-base-font-color px-2 py-1'}
                                                style={{
                                                    color: fontColor,
                                                    backgroundColor: highlightColor,
                                                }}
                                            />
                                        </div>
                                    </Flex>

                                    <RichText
                                        className='text-base-font-color !block pt-[24px] pb-[32px]'
                                        html={currentParties[0]?.partyDescription?.rendered || ''}
                                    />
                                    <div className='flex items-center'>
                                        <div className='mr-6'>
                                            <CustButton
                                                data={{
                                                    setting: {
                                                        // className: 'mr-6',
                                                        onClick: () => {
                                                            WATrackerClickEvn(
                                                                'RnE/HW_ClickBanner_Event-List',
                                                            );
                                                        },
                                                    },
                                                    buttonLink: getButtonLinkWithCID(
                                                        currentParties[0].ctaButtonUrl?.value,
                                                        `${contentData?.name}_Event-List`,
                                                    ),
                                                }}
                                            >
                                                <GeneralButtonType
                                                    label={
                                                        currentParties[0].ctaButtonText?.value || ''
                                                    }
                                                    icon={'arrowRightBK'}
                                                    iconColor={themeColor}
                                                />
                                            </CustButton>
                                        </div>

                                        {calendarData.length > 0 && (
                                            <AddToCalendar
                                                data={{
                                                    setting: {
                                                        label: t('atcbLabel'),
                                                        options: ['Apple', 'Google', 'Outlook.com'],
                                                        name: 'Title',
                                                        dates: calendarData,
                                                        iCalFileName: 'Event-confirmed',
                                                    },
                                                    modalHeadline: t('atcbLabel'),
                                                    modaldescription: '',
                                                    isLink: true,
                                                    cardFontColor: '#FFFFFF',
                                                    // cardThemeColor: themeColor,
                                                    clickEventID:
                                                        'RnE/HW_ClickAddToCalendar_Feature-Highlight',
                                                }}
                                            />
                                        )}
                                    </div>
                                </Flex>
                            </Col>
                        </Row>
                    )}

                    {/* Upcoming */}
                    {upcoming.length > 0 && (
                        <>
                            <div className='pt-[96px]'>
                                <Flex vertical gap={8} align='center'>
                                    <Heading
                                        className={'text-center text-base-font-color'}
                                        level={3}
                                        text={t('partiesUpcoming')}
                                    />
                                    <TitleIcon />
                                </Flex>
                            </div>
                            <Row gutter={[24, 24]} className='mt-16'>
                                {upcoming.map((i: any, index: number) => (
                                    <Col key={index} xs={24} md={6}>
                                        <PartyCardList
                                            data={i}
                                            type='UpcomingParties'
                                        ></PartyCardList>
                                    </Col>
                                ))}
                            </Row>
                        </>
                    )}

                    {/* Previous */}
                    <div className='pt-[96px]'>
                        <Flex vertical gap={8} align='center'>
                            <Heading
                                className={'text-center text-base-font-color'}
                                level={3}
                                text={t('partiesPrevious')}
                            />
                            <TitleIcon />
                        </Flex>
                    </div>
                    <Row gutter={[24, 24]} className='mt-16'>
                        {previous.map((i: any, index: number) => (
                            <Col key={index} xs={24} md={6}>
                                <PartyCardList
                                    data={i}
                                    type='RelivePreviousParties'
                                ></PartyCardList>
                            </Col>
                        ))}
                    </Row>
                </Flex>
            </Flex>
            <CrossSellBanner content={crossSellBannerData} currentThemeColor={themeColor} />
        </>
    );
};

export default PartiesPage;
